import type { AssignedWorkoutTree, TreeExercise, TreeSection } from "./assignedWorkoutTree";
import { dayCounts, writtenSets, type QSession, type QueueRow } from "./reviewQueue";
import type { SheetPlan, SheetSession } from "./reviewSheet";
import { cellUnitFor, parseCell, type NextCell } from "./reviewWrite";
import { planSections, type PlanTreeSectionRow } from "./setVerdict";
import { addDays, mondayOf } from "./reviewerWeek";
import { isPerformed } from "./progressHistory";
import type { WeekDay, WeekTree, WeekWorkout } from "./reviewerWeekVocabulary.generated";

/* THE REVIEWER SEAT, WILLIAM'S SIDE (step 2, 2026-10-10; `docs/REVIEWER-SEAT-BRIEF.md`) — the pure half.
 *
 * What a reviewer READS comes ONLY through the locked functions of `20261010220000`: `reviewer_links()`,
 * `reviewer_week(client, from, to)`, `reviewer_sessions(client, from, to)` (no policy on any week or training table —
 * W62). What he WRITES is his own rows: `reviewer_reviews` + `reviewer_review_items` (words, takes, SUGGESTED next-week
 * numbers as plain data) and `reviewer_week_proposals` (a whole proposed week). 🔴 NOTHING in the seat's files names a
 * `coach_*` table — the ruler `reviewerSeatNoCoachWrite.test.ts` reads every one of them. */

/* ── the links ────────────────────────────────────────────────────────────────────────────────────────────────────── */

/** One row of `reviewer_links()` (the generated return type, narrowed to what the page reads). */
export type ReviewerLink = {
  id: string; role: string; status: string; client_id: string; other_name: string | null;
  invited_at: string | null; accepted_at: string | null; ended_at: string | null;
};
/** The `Reviewing` group: the clients this account reviews (active) and the invitations waiting for it (pending). A
 *  client's OWN invitations (role `client`) are the phone's, step 3 — never drawn here. */
export type ReviewingRow = { linkId: string; clientId: string; name: string; state: "active" | "invited" };
export function reviewingRows(links: readonly ReviewerLink[]): ReviewingRow[] {
  return links
    .filter((l) => (l.role === "reviewer" && l.status === "active") || (l.role === "invitee" && l.status === "pending"))
    .map((l): ReviewingRow => ({ linkId: l.id, clientId: l.client_id, name: nameOr(l.other_name), state: l.role === "reviewer" ? "active" : "invited" }))
    .sort((a, b) => (a.state === b.state ? a.name.localeCompare(b.name) : a.state === "invited" ? -1 : 1));
}
export const nameOr = (n: string | null | undefined) => (n ?? "").trim() === "" ? "Your client" : (n ?? "").trim();
export const REVIEWING_TITLE = "Reviewing";
export const REVIEWING_HINT = "Weeks you review. You suggest; you never change them.";

/* ── the finished days (`reviewer_sessions`) ──────────────────────────────────────────────────────────────────────── */

type RawLog = { set_number: number | null; status: string; reps_completed: number | null; weight_kg: number | string | null; assist_kg?: number | string | null;
  hold_seconds: number | null; rpe: number | string | null; note: string | null; distance_m?: number | string | null; rounds?: number | null; completed_at?: string | null };
type RawClip = { id: string; set_number: number | null; media_kind: string; storage_path: string; recorded_at: string | null };
type RawWse = { id: string; position: number | null; section_role: string | null; coach_exercise_id: string | null; exercise_id: string | null;
  name: string | null; tracking_fields: string[] | null; set_logs: RawLog[] | null; form_clips: RawClip[] | null };
export type RawSession = { id: string; user_id: string; kind: string; scheduled_date: string | null; completed_at: string | null; status: string | null;
  duration_actual_minutes: number | null; performance_notes: string | null; workout_session_exercises: RawWse[] | null };

const num = (v: number | string | null | undefined): number | null => {
  if (v === null || v === undefined) return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

export function asSheetSession(s: RawSession): SheetSession {
  return {
    id: s.id, userId: s.user_id, kind: s.kind, scheduledDate: s.scheduled_date, minutes: s.duration_actual_minutes, note: s.performance_notes,
    exercises: (s.workout_session_exercises ?? []).map((w) => ({
      id: w.id, position: w.position, section_role: w.section_role, key: w.coach_exercise_id ?? w.exercise_id, coachExerciseId: w.coach_exercise_id,
      logs: (w.set_logs ?? []).map((l) => ({ set_number: l.set_number, status: l.status, reps_completed: l.reps_completed, weight_kg: num(l.weight_kg),
        hold_seconds: l.hold_seconds, rpe: num(l.rpe), note: l.note, distance_m: num(l.distance_m), rounds: l.rounds ?? null })),
      clips: w.form_clips ?? [],
    })),
  };
}
/** The names a session's own rows carry (`reviewer_sessions` returns them — the reviewer reads no library). */
export function sessionNames(s: RawSession): Map<string, string> {
  const m = new Map<string, string>();
  for (const w of s.workout_session_exercises ?? []) {
    const k = w.coach_exercise_id ?? w.exercise_id;
    if (k !== null && w.name !== null) m.set(k, w.name);
  }
  return m;
}
/** Performed sets (`SetLogKind.performed`, the one positive list `progressHistory.isPerformed` holds). */
export const loggedSets = (s: RawSession) =>
  (s.workout_session_exercises ?? []).reduce((n, w) => n + (w.set_logs ?? []).filter((l) => isPerformed(l.status)).length, 0);
/** A session counts as FINISHED once it was completed or holds a logged set (the queue's "finished": a set log, never a
 *  status a button has to set — the codex, vocabulary-mismatch 2026-09-13). */
export const finished = (s: RawSession) => s.completed_at !== null || s.status === "completed" || loggedSets(s) > 0;

/** The day's ONE training workout of the written week, as the sheet's plan (0 or 2+ → no plan: the queue's own rule). */
export function planOfDay(week: WeekTree, day: string): { plan: SheetPlan; workout: WeekWorkout } | null {
  const days = week.filter((d) => d.scheduled_date === day && d.coach_workouts.kind !== "task");
  const one = days.length === 1 ? days[0] : undefined;
  if (one === undefined) return null;
  return { workout: one.coach_workouts, plan: { workoutName: one.coach_workouts.name, sections: planSections(one.coach_workouts.coach_workout_sections as unknown as PlanTreeSectionRow[]) } };
}
export function namesOfWeek(week: WeekTree): Map<string, string> {
  const m = new Map<string, string>();
  for (const d of week) for (const s of d.coach_workouts.coach_workout_sections) for (const e of s.coach_workout_exercises) if (e.exercise_id !== null) m.set(e.exercise_id, e.name);
  return m;
}
/** The km keys: an exercise whose field is `distance_km` (`tracking_fields`, which the reads carry). */
export function kmKeysOf(week: WeekTree, s: RawSession | null): Set<string> {
  const k = new Set<string>();
  for (const d of week) for (const sec of d.coach_workouts.coach_workout_sections) for (const e of sec.coach_workout_exercises)
    if (e.exercise_id !== null && (e.tracking_fields ?? []).includes("distance_km")) k.add(e.exercise_id);
  for (const w of s?.workout_session_exercises ?? []) if (w.coach_exercise_id !== null && (w.tracking_fields ?? []).includes("distance_km")) k.add(w.coach_exercise_id);
  return k;
}

/** The sheet's head row for one finished day — what the coach's queue would say, derived from the session alone. */
export function reviewerQueueRow(s: RawSession, clientName: string, workout: string | null, writtenSets: number | null): QueueRow {
  // The queue's own counts (`dayCounts`: main-section working sets), so the head reads as the coach's would.
  const q: QSession = {
    id: s.id, userId: s.user_id, kind: s.kind, completedAt: s.completed_at, scheduledDate: s.scheduled_date, createdAt: null,
    exercises: (s.workout_session_exercises ?? []).map((w) => ({
      id: w.id, position: w.position, section_role: w.section_role, key: w.coach_exercise_id ?? w.exercise_id, name: w.name ?? "", clips: 0,
      logs: (w.set_logs ?? []).map((l) => ({ status: l.status, completed_at: l.completed_at ?? null, rpe: num(l.rpe), note: l.note, set_number: l.set_number,
        reps_completed: l.reps_completed, weight_kg: num(l.weight_kg), hold_seconds: l.hold_seconds, distance_m: num(l.distance_m), rounds: l.rounds ?? null })),
    })),
  };
  const c = dayCounts(q, writtenSets);
  const finish = s.completed_at !== null ? Date.parse(s.completed_at) : Date.parse(`${s.scheduled_date ?? "1970-01-01"}T12:00:00Z`);
  return {
    sessionId: s.id, clientId: s.user_id, name: clientName, firstName: clientName.split(" ")[0] ?? clientName, workout, finish, done: c.done,
    written: writtenSets, skipped: c.skipped, notLogged: c.notLogged, short: null,
    notes: 0, clips: 0, flag: null, flags: [], reviewed: false, thisWeek: false, skippedKeys: [], notLoggedKeys: [], reviewedAt: null, seen: false,
  };
}
/** The queue's written count (`writtenSets`: main sections, working sets). */
export const writtenWorkingSets = (plan: SheetPlan | null) => (plan === null ? null : writtenSets(plan.sections));

/* ── next week: the written week's same day, a week on — the reviewer's cells are SUGGESTIONS ─────────────────────── */

/** A week-tree workout in the overlay reader's shape, so `reviewWrite.nextWeekCells` pairs it exactly as it pairs the
 *  coach's copies. The tree carries no set ids (none are written), so a set's id is `<exercise row>#<position>`. */
export function asAssignedTree(w: WeekWorkout, key: string): AssignedWorkoutTree {
  return {
    id: w.id ?? key, name: w.name, description: w.description,
    coach_workout_sections: w.coach_workout_sections.map((s, si): TreeSection => ({
      id: `${key}:s${String(si)}`, position: s.position, name: s.name, format: s.format, section_type: s.section_type,
      format_config: s.format_config, description: s.description, is_implicit: null,
      coach_workout_exercises: s.coach_workout_exercises.map((e, ei): TreeExercise => {
        const eid = e.id ?? `${key}:s${String(si)}e${String(ei)}`;
        return {
          id: eid, exercise_id: e.exercise_id, position: e.position, each_side: e.each_side, hold_until_lost: e.hold_until_lost, tempo: e.tempo,
          notes: e.notes, superset_with_next: e.superset_with_next, coach_exercises: { name: e.name },
          coach_workout_sets: e.coach_workout_sets.map((t, ti) => ({
            id: `${eid}#${String(t.position ?? ti)}`, position: t.position, reps: t.reps, weight_kg: t.weight_kg, time_seconds: t.time_seconds,
            rounds: t.rounds, rest_seconds: t.rest_seconds, set_type: t.set_type, distance_m: t.distance_m,
          })),
        };
      }),
    })),
  };
}
/** Next week's counterpart of a day: the SAME weekday a week on, when it holds one training workout. The written weeks are
 *  weekday-stable (every week script clones by weekday); a reviewer reads no template id to do better. */
export function nextDayOf(nextWeek: WeekTree, day: string): WeekDay | null {
  const d = addDays(day, 7);
  const days = nextWeek.filter((x) => x.scheduled_date === d && x.coach_workouts.kind !== "task");
  return days.length === 1 ? (days[0] ?? null) : null;
}
/** The two reads a day's review needs: its own week, and the next one (one call, ≤ 14 days — the function's cap is 62). */
export const weeksAround = (day: string) => ({ from: mondayOf(day), to: addDays(mondayOf(day), 13) });

/** One set's suggestion, as `reviewer_review_items.suggested` holds it: the table's set label and the ONE field the cell
 *  is about, in the database's column name — `{"set":"1","weight_kg":42.5}`, `{"set":"W1","reps":5}`. */
export type Suggestion = { set: string } & Partial<Record<"weight_kg" | "reps" | "time_seconds", number>>;
const COLUMN = { weightKg: "weight_kg", reps: "reps", holdSeconds: "time_seconds" } as const;

/** The typed cells read: a suggestion for every cell whose number differs from what the written next week holds;
 *  `invalid` names the cells that cannot be read (and then nothing is saved). */
export function suggestionsOf(rows: readonly { label: string; cell: NextCell | undefined }[], drafts: Readonly<Record<string, string>>):
  { suggested: Suggestion[]; invalid: string[] } {
  const suggested: Suggestion[] = [];
  const invalid: string[] = [];
  for (const { label, cell } of rows) {
    if (cell === undefined || cell.frozen) continue;
    const text = drafts[cell.setId];
    if (text === undefined || text.trim() === "") continue;
    const v = parseCell(cell.field, text, cellUnitFor(cell));
    if (v === "invalid") { invalid.push(cell.setId); continue; }
    if (v === cell.value) continue;
    suggested.push({ set: label, [COLUMN[cell.field]]: v });
  }
  return { suggested, invalid };
}
/** A saved draft's suggestions, back into the cells' typed text (by set label). */
export function draftsFromSuggested(rows: readonly { label: string; cell: NextCell | undefined }[], suggested: readonly Suggestion[] | null): Record<string, string> {
  const out: Record<string, string> = {};
  for (const s of suggested ?? []) {
    const row = rows.find((r) => r.label === s.set);
    const cell = row?.cell;
    if (cell === undefined) continue;
    const v = s[COLUMN[cell.field]];
    if (typeof v !== "number") continue;
    out[cell.setId] = cellUnitFor(cell) === "min" ? String(Math.round((v / 60) * 100) / 100) : String(v);
  }
  return out;
}
/** A suggestion read aloud — the client's phone and the week writer read the same words. */
export function suggestionWords(s: Suggestion): string {
  const parts: string[] = [];
  if (s.weight_kg !== undefined) parts.push(`${String(s.weight_kg)} kg`);
  if (s.reps !== undefined) parts.push(`${String(s.reps)} reps`);
  if (s.time_seconds !== undefined) parts.push(`${String(s.time_seconds)} s`);
  return `Set ${s.set}: ${parts.join(" · ")}`;
}

/* ── what the page shows about his own work ───────────────────────────────────────────────────────────────────────── */

export type MyReview = { id: string; session_id: string | null; scheduled_date: string; body: string | null; sent_at: string | null;
  audio_path: string | null; audio_seconds: number | null; video_path: string | null; video_seconds: number | null; photo_path: string | null };
export type MyItem = { id: string; review_id: string; workout_session_exercise_id: string | null; position: number; exercise_name: string | null;
  body: string | null; suggested: unknown; audio_path: string | null; audio_seconds: number | null; video_path: string | null; video_seconds: number | null; photo_path: string | null };
export type MyProposal = { id: string; week_start: string; week: unknown; note: string | null; sent_at: string | null; seen_at: string | null; updated_at: string };

/** NOTHING WAITS ON THE REVIEWER (his word 2026-10-11: *"like real life something can happen to him and so he couldn't do it"*):
 *  a day nobody reviewed within a week reads `Missed` — still open, never nagging, and the client's week never stalls on it. */
export const MISSED_AFTER_DAYS = 7;
export function dayState(review: MyReview | undefined, day: string | null = null, today: string | null = null): "sent" | "draft" | "toReview" | "missed" {
  if (review !== undefined) return review.sent_at !== null ? "sent" : "draft";
  return day !== null && today !== null && day.slice(0, 10) < addDays(today, -MISSED_AFTER_DAYS) ? "missed" : "toReview";
}
export const DAY_STATE_WORDS = { sent: "Reviewed", draft: "Draft", toReview: "To review", missed: "Missed" } as const;
/** The week his pencil opens: the week after the reviewed day — or, when that week has already started, the one after
 *  today (a late review never proposes a week the client is already training). */
export function proposalMonday(nextDay: string | null, day: string, today: string): string {
  const after = mondayOf(nextDay ?? addDays(day, 7));
  const earliest = addDays(mondayOf(today), 7);
  return after < earliest ? earliest : after;
}
/* THE DASHBOARD'S `Reviewing` ROW (his question 2026-10-11: *"I'm not gonna be in his dashboard?"*): per client he reviews,
 * the finished days still open to him — not sent, and not yet `Missed` (a draft is still to review). READ-ONLY: the days
 * are `reviewer_sessions`, his own reviews say which are sent; no coach queue item, no write path. */
export function daysToReview(sessions: readonly RawSession[], reviews: readonly Pick<MyReview, "session_id" | "sent_at">[], today: string): number {
  const sent = new Set(reviews.filter((r) => r.sent_at !== null).map((r) => r.session_id));
  return sessions.filter((s) => finished(s) && !sent.has(s.id) && s.scheduled_date !== null
    && dayState(undefined, s.scheduled_date, today) === "toReview" && s.scheduled_date.slice(0, 10) <= today).length;
}
export const toReviewWords = (n: number) => n === 0 ? "Nothing to review" : `${String(n)} ${n === 1 ? "day" : "days"} to review`;
export const PROPOSAL_WORDS =(p: MyProposal | undefined) => p === undefined ? "None yet" : p.sent_at !== null ? "Sent" : "Draft";
export const SENT_FINAL = "Sent. A sent review is final.";
export const PROPOSAL_SENT_FINAL = "Sent. A sent week is final.";

/** Reads `suggested` back as suggestions, dropping anything that is not one (a failed read is never a guess). */
export function readSuggested(v: unknown): Suggestion[] {
  if (!Array.isArray(v)) return [];
  return v.filter((x): x is Suggestion => x !== null && typeof x === "object" && typeof (x as { set?: unknown }).set === "string");
}
